import { Alert, Button, Collapse, Group, Loader, Stack, Text, Title, toast } from '@common/ui'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import React, { useCallback, useState } from 'react'
import { com } from '@eidu/web'
import { useSortedEntityTypes } from '../../io/useEntityTypes'
import { usePermissionGrant } from '../../io/usePermissionGrants'
import { PermissionGrantDTO } from '../../domain/permission/PermissionGrantDTO'
import {
  useCreatePermissionGrant,
  useDeletePermissionGrant,
  useModifyPermissionGrant,
} from '../../io/usePermissionGrantMutations'
import { useInvalidatePermissionGrants } from '../../io/usePaginatedPermissionGrants'
import { usePermissionPoliciesByIds, usePermissionPoliciesInfinite } from '../../io/usePermissionPolicies'
import { useCreatePermissionPolicy } from '../../io/usePermissionPolicyMutations'
import { requireNotUndefinedOrNull } from '../../util/require'
import PermissionError from '../entity/PermissionError'
import { PermissionWithId } from '../../components/input/PermissionsInput'
import ConfirmationDialog from '../../components/layout/ConfirmationDialog'
import PermissionGrantForm from '../../components/permissions/PermissionGrantForm'
import SaveAsPolicyDialog from '../../components/layout/SaveAsPolicyDialog'
import AuditLogDrawer from '../audit/AuditLogDrawer'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import EntityType = com.eidu.entity.type.EntityType
import permissionGrantIdFromString = com.eidu.entity.permission.permissionGrantIdFromString
import PermissionGrantId = com.eidu.entity.permission.PermissionGrantId
import EntitySelector = com.eidu.entity.selector.EntitySelector
import useHasGlobalPermissions from './useHasGlobalPermissions'
import GlobalAction = com.eidu.entity.permission.GlobalAction
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import PermissionPolicyId = com.eidu.entity.permission.PermissionPolicyId

type CreateOrEditPermissionGrantPageContentProps = {
  entityTypes: readonly EntityType[]
  userSelector: EntitySelector
  setUserSelector: (id: EntitySelector) => void
  selectedPermissions: readonly PermissionWithId[]
  setSelectedPermissions: (permissions: readonly PermissionWithId[]) => void
  selectedPolicyIds: ReadonlyArray<PermissionPolicyId>
  setSelectedPolicyIds: (ids: ReadonlyArray<PermissionPolicyId>) => void
  loadedPolicies: ReadonlyArray<PermissionPolicyDTO>
  selectedPolicies: ReadonlyArray<PermissionPolicyDTO>
  hasMorePolicies: boolean
  loadMorePolicies: () => void
  policiesLoading: boolean
  onSubmit: () => Promise<void>
  onSaveAsPolicy: () => void
  onDelete?: () => Promise<void>
  onCancel: () => void
  isEditMode: boolean
  submitting: boolean
  permissionGrantId?: string
}

const CreateOrEditPermissionGrantPageContent = ({
  entityTypes,
  userSelector,
  setUserSelector,
  selectedPermissions,
  setSelectedPermissions,
  selectedPolicyIds,
  setSelectedPolicyIds,
  loadedPolicies,
  selectedPolicies,
  hasMorePolicies,
  loadMorePolicies,
  policiesLoading,
  onSubmit,
  onSaveAsPolicy,
  onDelete,
  onCancel,
  isEditMode,
  submitting,
  permissionGrantId,
}: CreateOrEditPermissionGrantPageContentProps) => (
  <Stack p="lg">
    <Group justify="space-between" gap="xs">
      <Title order={2}>{isEditMode ? 'Update permission' : 'Grant permission'}</Title>
      <Group gap="xs">
        {isEditMode && permissionGrantId && <AuditLogDrawer targetId={permissionGrantId} />}
        {isEditMode && onDelete && (
          <ConfirmationDialog
            title="Are you sure you want to delete these permissions? This action cannot be undone."
            content={<Text>Delete</Text>}
            onConfirm={onDelete}
            disabled={submitting}
          />
        )}
      </Group>
    </Group>
    <div style={{ paddingBlock: 'var(--trellis-spacing-md)' }}>
      <PermissionGrantForm
        entityTypes={entityTypes}
        userSelector={userSelector}
        setUserSelector={setUserSelector}
        selectedPermissions={selectedPermissions}
        setSelectedPermissions={setSelectedPermissions}
        selectedPolicyIds={selectedPolicyIds}
        setSelectedPolicyIds={setSelectedPolicyIds}
        loadedPolicies={loadedPolicies}
        selectedPolicies={selectedPolicies}
        hasMorePolicies={hasMorePolicies}
        loadMorePolicies={loadMorePolicies}
        policiesLoading={policiesLoading}
      />
    </div>
    <Group justify="flex-end" gap="md" p="md">
      <Button variant="outline" onClick={onSaveAsPolicy} disabled={submitting || selectedPermissions.length === 0}>
        Save as policy
      </Button>
      <Button variant="default" onClick={onCancel} disabled={submitting}>
        Cancel
      </Button>
      <Button onClick={onSubmit} disabled={submitting} loading={submitting}>
        {submitting ? 'Saving…' : 'Save'}
      </Button>
    </Group>
  </Stack>
)

type PermissionGrantFormPageProps = {
  entityTypes: readonly EntityType[]
  permissionGrantId: PermissionGrantId | undefined
  initialGrant: PermissionGrantDTO | null
  loadError: unknown
}

const defaultUserSelector = (entityTypes: readonly EntityType[]) =>
  new EntitySelector.OfType(
    entityTypes
      .filter((type) => type.kind.equals(EntityTypeKind.Companion.User))
      .sort((a, b) => a.name.localeCompare(b.name))[0].id
  )

const grantToSelectedPermissions = (grant: PermissionGrantDTO): readonly PermissionWithId[] =>
  [...grant.permissionGrant.permissions.asJsReadonlySetView()].map((permission) => ({
    id: crypto.randomUUID(),
    permission,
  }))

const grantToSelectedPolicyIds = (grant: PermissionGrantDTO): ReadonlyArray<PermissionPolicyId> => [
  ...grant.permissionGrant.policyIds.asJsReadonlySetView(),
]

const PermissionGrantFormPage = ({
  entityTypes,
  permissionGrantId,
  initialGrant,
  loadError,
}: PermissionGrantFormPageProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = permissionGrantId !== undefined
  const invalidateGrants = useInvalidatePermissionGrants()

  const [submitting, setSubmitting] = useState(false)
  const [saveAsPolicyOpen, setSaveAsPolicyOpen] = useState(false)
  const createMutation = useCreatePermissionGrant()
  const modifyMutation = useModifyPermissionGrant()
  const deleteMutation = useDeletePermissionGrant()
  const policiesInfinite = usePermissionPoliciesInfinite(50)
  const createPolicyMutation = useCreatePermissionPolicy()

  const userTypes = entityTypes.filter((type) => type.kind.equals(EntityTypeKind.Companion.User))
  const [userSelector, setUserSelector] = useState<EntitySelector>(() => {
    if (initialGrant) return initialGrant.permissionGrant.userSelector
    if (userTypes.length === 0) return EntitySelector.All
    const initialUserTypeId = (location.state as { initialUserTypeId?: string } | null)?.initialUserTypeId
    if (initialUserTypeId) {
      const preselected = userTypes.find((type) => type.id.toString() === initialUserTypeId)
      if (preselected) return new EntitySelector.OfType(preselected.id)
    }
    return defaultUserSelector(entityTypes)
  })
  const [selectedPermissions, setSelectedPermissions] = useState<readonly PermissionWithId[]>(() =>
    initialGrant ? grantToSelectedPermissions(initialGrant) : []
  )
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<ReadonlyArray<PermissionPolicyId>>(() =>
    initialGrant ? grantToSelectedPolicyIds(initialGrant) : []
  )
  const selectedPoliciesQuery = usePermissionPoliciesByIds(selectedPolicyIds)

  const { hasPermission } = useHasGlobalPermissions(GlobalAction.ManagePermissionGrants)
  const hasManagePermissionGrantsPermission = hasPermission(GlobalAction.ManagePermissionGrants)

  const submit = useCallback(async () => {
    setSubmitting(true)
    try {
      if (userSelector) {
        if (selectedPermissions.length === 0 && selectedPolicyIds.length === 0) {
          toast.negative('Add at least one permission or policy.')
          return
        }
        const permissions = selectedPermissions.map(({ permission }) => permission)
        if (permissionGrantId) {
          await modifyMutation.mutateAsync({
            id: permissionGrantId,
            userSelector,
            permissions,
            policyIds: selectedPolicyIds,
          })
          toast.positive(`Permission Updated`)
        } else {
          await createMutation.mutateAsync({ userSelector, permissions, policyIds: selectedPolicyIds })
          toast.positive(`Permission Created`)
        }
        await invalidateGrants()
        navigate(-1)
      }
    } catch (e) {
      console.error(e)
      toast.negative(`Failed to ${isEditMode ? 'update' : 'grant'}`)
    } finally {
      setSubmitting(false)
    }
  }, [
    userSelector,
    selectedPermissions,
    selectedPolicyIds,
    isEditMode,
    permissionGrantId,
    createMutation,
    modifyMutation,
  ])

  const onCancel = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const onDelete = useCallback(async () => {
    if (!permissionGrantId) return
    setSubmitting(true)
    try {
      await deleteMutation.mutateAsync(permissionGrantId)
      await invalidateGrants()
      toast.positive(`Deleted permission`)
      navigate(-1)
    } catch (e) {
      console.error(e)
      toast.negative(`Failed to delete permission`)
    } finally {
      setSubmitting(false)
    }
  }, [permissionGrantId, deleteMutation])

  const onSaveAsPolicyConfirm = useCallback(
    async (name: string) => {
      try {
        await createPolicyMutation.mutateAsync({
          name,
          permissions: selectedPermissions.map(({ permission }) => permission),
        })
        toast.positive(`Policy "${name}" created`)
        setSaveAsPolicyOpen(false)
      } catch (e) {
        console.error(e)
        toast.negative(`Failed to save policy`)
      }
    },
    [selectedPermissions, createPolicyMutation]
  )

  return (
    <Stack>
      <Collapse expanded={!!loadError}>
        <Alert
          variant="filled"
          severity="negative"
          m="md"
          action={
            <Button variant="white" size="xs" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          }
        >
          <div>{String(loadError)}</div>
          <div>Please reload the page and try again.</div>
        </Alert>
      </Collapse>
      {!loadError && hasManagePermissionGrantsPermission && userTypes.length === 0 && (
        <Alert severity="caution" m="md">
          No user-kind entity types are configured. Create a user type before granting permissions.
        </Alert>
      )}
      {!loadError && hasManagePermissionGrantsPermission && userTypes.length > 0 && (
        <CreateOrEditPermissionGrantPageContent
          entityTypes={entityTypes}
          userSelector={userSelector}
          setUserSelector={setUserSelector}
          selectedPermissions={selectedPermissions}
          setSelectedPermissions={setSelectedPermissions}
          selectedPolicyIds={selectedPolicyIds}
          setSelectedPolicyIds={setSelectedPolicyIds}
          loadedPolicies={policiesInfinite.policies}
          selectedPolicies={selectedPoliciesQuery.policies}
          hasMorePolicies={policiesInfinite.hasMore}
          loadMorePolicies={policiesInfinite.loadMore}
          policiesLoading={policiesInfinite.loading || selectedPoliciesQuery.isLoading}
          onSubmit={submit}
          onSaveAsPolicy={() => setSaveAsPolicyOpen(true)}
          onDelete={isEditMode ? onDelete : undefined}
          onCancel={onCancel}
          isEditMode={isEditMode}
          submitting={submitting}
          permissionGrantId={permissionGrantId?.toString()}
        />
      )}
      <SaveAsPolicyDialog
        open={saveAsPolicyOpen}
        permissionCount={selectedPermissions.length}
        onCancel={() => setSaveAsPolicyOpen(false)}
        onConfirm={onSaveAsPolicyConfirm}
      />
      {!submitting && !hasManagePermissionGrantsPermission && (
        <PermissionError missingPermittedActions={[GlobalAction.ManagePermissionGrants]} />
      )}
    </Stack>
  )
}

type EditPermissionGrantPageProps = {
  entityTypes: readonly EntityType[]
  permissionGrantId: PermissionGrantId
}

const EditPermissionGrantPage = ({ entityTypes, permissionGrantId }: EditPermissionGrantPageProps) => {
  const { permissionGrant, isLoading, error } = usePermissionGrant(permissionGrantId)
  if (isLoading) return <Loader m="xl" />
  return (
    <PermissionGrantFormPage
      entityTypes={entityTypes}
      permissionGrantId={permissionGrantId}
      initialGrant={permissionGrant ?? null}
      loadError={error}
    />
  )
}

type CreateOrEditPermissionGrantPageProps = {
  entityTypes: readonly EntityType[]
  isEditMode: boolean
}

const CreateOrEditPermissionGrantPage = ({ entityTypes, isEditMode }: CreateOrEditPermissionGrantPageProps) => {
  const params = useParams()
  if (isEditMode) {
    const permissionGrantId = permissionGrantIdFromString(requireNotUndefinedOrNull(params.permissionGrantId))
    return <EditPermissionGrantPage entityTypes={entityTypes} permissionGrantId={permissionGrantId} />
  }
  return (
    <PermissionGrantFormPage
      entityTypes={entityTypes}
      permissionGrantId={undefined}
      initialGrant={null}
      loadError={null}
    />
  )
}

const CreateOrEditPermissionGrantPageRoute = ({ isEditMode }: { isEditMode: boolean }) => {
  const entityTypes = useSortedEntityTypes() ?? []

  if (entityTypes.length === 0) return null
  return <CreateOrEditPermissionGrantPage entityTypes={entityTypes} isEditMode={isEditMode} />
}

export { CreateOrEditPermissionGrantPage }
export default CreateOrEditPermissionGrantPageRoute
